import React, { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid2,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import { useNotification } from "../../../utils/NotificationsContext";
import {
  LeadAnalyticsFilters,
} from "../../../models/Dashboard/LeadAnalytics/LeadAnalyticsFilters";
import LeadStageSummaryTable from "../../components/tables/LeadStageSummaryTable";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  LEAD_SECTION_FILTER_OPTIONS,
  LeadPipelineStatusSummaryGroup,
  LeadSection,
  LeadStageAverages,
  LeadStatusSummaryItem,
} from "../../../models/Dashboard/LeadAnalytics/LeadAnalytics";
import { SortType } from "../../../models/SortType";

const defaultAverages: LeadStageAverages = {
  mql: 0,
  non_contactable: 0,
  open: 0,
  total_time: 0,
};

const getSectionLabel = (section: LeadSection) => {
  if (section === "open") return "Open Leads";
  if (section === "closed") return "Closed Leads";
  return "All Leads";
};

const leadSectionFilterList = LEAD_SECTION_FILTER_OPTIONS.map((option) => ({
  id: option.id,
  value: option.value,
}));

const renderStatusSummaryCards = (
  pipelineId: number,
  items: LeadStatusSummaryItem[],
  selectedPipelineId: number | null,
  selectedFinalStatus: string | null,
  onStatusClick: (pipelineId: number, status: string | null) => void
) =>
  items.map((item, index) => {
    const hue = Math.round((index * 360) / Math.max(items.length, 1));
    const borderColor = `hsl(${hue}, 55%, 78%)`;
    const cardBg = `linear-gradient(180deg, #FFFFFF 0%, hsl(${hue}, 70%, 96%) 100%)`;
    const badgeBg = `hsl(${hue}, 65%, 92%)`;
    const badgeText = `hsl(${hue}, 45%, 34%)`;
    const clickedStatus = item.final_status || null;
    const isSelected =
      selectedPipelineId === pipelineId && selectedFinalStatus === clickedStatus;

    return (
      <Grid2
        key={`${pipelineId}-${item.final_status}-${index}`}
        size={{ xs: 12, sm: 6, md: 3, lg: 2 }}
      >
        <Paper
          variant="outlined"
          onClick={() => {
            onStatusClick(pipelineId, isSelected ? null : clickedStatus);
          }}
          sx={{
            p: 1.1,
            borderRadius: 2,
            height: "100%",
            minHeight: 78,
            display: "flex",
            flexDirection: "column",
            borderColor,
            background: cardBg,
            cursor: "pointer",
            boxShadow: isSelected ? "0 0 0 1.5px #6455ED inset" : "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <CustomTypography variant="heading8" sx={{ fontSize: "0.78rem", color: "#5C6785" }}>
              {item.final_status || "-"}
            </CustomTypography>
          </Box>
          <Box
            sx={{
              mt: "auto",
              pt: 0.8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <CustomTypography
              variant="heading7"
              sx={{ fontSize: "0.92rem", fontWeight: 700, color: "#1E2A44" }}
            >
              {item.lead_count}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: badgeText,
                px: 0.75,
                py: 0.2,
                borderRadius: 1,
                bgcolor: badgeBg,
              }}
            >
              {item.lead_percent.toFixed(2)}%
            </CustomTypography>
          </Box>
        </Paper>
      </Grid2>
    );
  });

const LeadAnalyticsDashboard: React.FC = () => {
  const { userStore, leadAnalyticsStore } = useStores();
  const { showError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();

  const loading = leadAnalyticsStore.loading;
  const filters = leadAnalyticsStore.filters;
  const selectedFinalStatus = leadAnalyticsStore.selectedFinalStatus;
  const selectedStagePipelineId = leadAnalyticsStore.selectedStagePipelineId;
  const isFiltersExpanded = leadAnalyticsStore.isFiltersExpanded;

  const fetchData = useCallback(async (currentFilters: LeadAnalyticsFilters) => {
    leadAnalyticsStore.setLoading(true);
    leadAnalyticsStore.fetchLeadStageSummary(currentFilters).then(() => {
      leadAnalyticsStore.setLoading(false);
    }).catch((error) => {
      showError("Error", error?.message || "Failed to fetch lead stage summary");
      leadAnalyticsStore.setLoading(false);
    });

  }, [leadAnalyticsStore]);

  const ownerOptions = useMemo(() => {
    const uniqueOwners = new Map<string, FilterValue>();
    // uniqueOwners.set("", { id: "", value: "All Owners" });

    leadAnalyticsStore.owners?.forEach((owner) => {
      const ownerId = String(owner.id ?? "");
      if (!uniqueOwners.has(ownerId)) {
        uniqueOwners.set(ownerId, { ...owner, id: ownerId });
      }
    });

    return Array.from(uniqueOwners.values());
  }, [leadAnalyticsStore.owners ?? []]);

  const pipelineOptions = useMemo(() => {
    const uniquePipelines = new Map<string, FilterValue>();
    leadAnalyticsStore.pipelines?.forEach((pipeline) => {
      const pipelineId = String(pipeline.id ?? "");
      if (!uniquePipelines.has(pipelineId)) {
        uniquePipelines.set(pipelineId, { ...pipeline, id: pipelineId });
      }
    });
    return Array.from(uniquePipelines.values());
  }, [leadAnalyticsStore.pipelines ?? []]);

  const sourceOptions = useMemo(() => {
    const uniqueSources = new Map<string, FilterValue>();
    leadAnalyticsStore.sources?.forEach((source) => {
      const sourceId = String(source.id ?? "");
      if (!uniqueSources.has(sourceId)) {
        uniqueSources.set(sourceId, { ...source, id: sourceId });
      }
    });
    return Array.from(uniqueSources.values());
  }, [leadAnalyticsStore.sources ?? []]);

  const handleOwnerChange = useCallback((ids: string[]) => {
    const normalizedIds = ids.filter((id) => id !== "");
    leadAnalyticsStore.updateFilters((prev) => {
      if (
        prev.owner_id.length === normalizedIds.length &&
        prev.owner_id.every((id, index) => id === normalizedIds[index])
      ) {
        return prev;
      }
      return { ...prev, owner_id: normalizedIds };
    });
  }, [leadAnalyticsStore]);

  const handlePipelineChange = useCallback((ids: string[]) => {
    const normalizedIds = ids?.filter((id) => id !== "") || [];
    leadAnalyticsStore.updateFilters((prev) => {
      if (
        prev.pipeline_id.length === normalizedIds.length &&
        prev.pipeline_id.every((id, index) => id === normalizedIds[index])
      ) {
        return prev;
      }
      return { ...prev, pipeline_id: normalizedIds };
    });
  }, [leadAnalyticsStore]);

  const handleSourceChange = useCallback((ids: string[]) => {
    const normalizedIds = ids?.filter((id) => id !== "") || [];
    leadAnalyticsStore.updateFilters((prev) => {
      if (
        prev.source_id.length === normalizedIds.length &&
        prev.source_id.every((id, index) => id === normalizedIds[index])
      ) {
        return prev;
      }
      return { ...prev, source_id: normalizedIds };
    });
  }, [leadAnalyticsStore]);

  const selectedOwners = useMemo(
    () =>
      ownerOptions.filter(
        (owner) => owner.id !== "" && filters.owner_id?.includes(String(owner.id))
      ),
    [filters.owner_id, ownerOptions]
  );

  const selectedPipelines = useMemo(
    () =>
      pipelineOptions.filter(
        (pipeline) => filters.pipeline_id?.includes(String(pipeline.id))
      ),
    [filters.pipeline_id, pipelineOptions]
  );

  const selectedSources = useMemo(
    () =>
      sourceOptions.filter(
        (source) => filters.source_id?.includes(String(source.id))
      ),
    [filters.source_id, sourceOptions]
  );

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_SALES_DASHBOARD])) {
      return;
    }

    const defaults = leadAnalyticsStore.filters;
    const ownerIdParam = searchParams.get("owner_id");
    const pipelineIdParam = searchParams.get("pipeline_id");
    const sourceIdParam = searchParams.get("source_id");
    const startDateParam = searchParams.get("start_date");
    const endDateParam = searchParams.get("end_date");
    const sectionParam = searchParams.get("section");
    const pageNoParam = searchParams.get("page_no");
    const limitParam = searchParams.get("limit");
    const sortByParam = searchParams.get("sort_by");
    const sortTypeParam = searchParams.get("sort_type");
    const finalStatusParam = searchParams.get("final_status");
    const stagePipelineIdParam = searchParams.get("stage_pipeline_id");

    const hydratedSection: LeadSection =
      sectionParam === "open" || sectionParam === "closed" ? sectionParam : "all";

    const hydratedFilters: LeadAnalyticsFilters = {
      owner_id: ownerIdParam ? ownerIdParam.split(",").filter(Boolean) : defaults.owner_id,
      pipeline_id: pipelineIdParam
        ? pipelineIdParam.split(",").filter(Boolean)
        : defaults.pipeline_id,
      source_id: sourceIdParam ? sourceIdParam.split(",").filter(Boolean) : defaults.source_id,
      final_status: finalStatusParam || null,
      stage_pipeline_id: stagePipelineIdParam
        ? Number(stagePipelineIdParam) || null
        : defaults.stage_pipeline_id,
      start_date: startDateParam ? dayjs(startDateParam) : defaults.start_date,
      end_date: endDateParam ? dayjs(endDateParam) : defaults.end_date,
      section: hydratedSection,
      page_no: pageNoParam ? Number(pageNoParam) || defaults.page_no : defaults.page_no,
      limit: limitParam ? Number(limitParam) || defaults.limit : defaults.limit,
      sort_by: sortByParam || defaults.sort_by,
      sort_type:
        sortTypeParam === SortType.ASC || sortTypeParam === SortType.DESC
          ? sortTypeParam
          : defaults.sort_type,
    };

    leadAnalyticsStore.setFilters(hydratedFilters);
    leadAnalyticsStore.setSelectedFinalStatus(finalStatusParam || null);
    leadAnalyticsStore.setSelectedStagePipelineId(
      stagePipelineIdParam ? Number(stagePipelineIdParam) || null : null
    );

    void leadAnalyticsStore.fetchOwners();
    void leadAnalyticsStore.fetchPipelines().catch((error) => {
      showError("Error", error?.message || "Failed to fetch pipelines");
    });
    void leadAnalyticsStore.fetchSources().catch((error) => {
      showError("Error", error?.message || "Failed to fetch sources");
    });
    void fetchData({
      ...hydratedFilters,
      final_status: finalStatusParam || null,
      stage_pipeline_id: stagePipelineIdParam
        ? Number(stagePipelineIdParam) || null
        : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (filters.owner_id.length > 0) {
      nextParams.set("owner_id", filters.owner_id.join(","));
    }
    if (filters.pipeline_id.length > 0) {
      nextParams.set("pipeline_id", filters.pipeline_id.join(","));
    }
    if (filters.source_id.length > 0) {
      nextParams.set("source_id", filters.source_id.join(","));
    }
    if (filters.start_date) nextParams.set("start_date", filters.start_date.format("YYYY-MM-DD"));
    if (filters.end_date) nextParams.set("end_date", filters.end_date.format("YYYY-MM-DD"));
    nextParams.set("section", filters.section);
    nextParams.set("page_no", String(filters.page_no));
    nextParams.set("limit", String(filters.limit));
    nextParams.set("sort_by", filters.sort_by);
    nextParams.set("sort_type", filters.sort_type);
    if (selectedFinalStatus) {
      nextParams.set("final_status", selectedFinalStatus);
    }
    if (selectedStagePipelineId) {
      nextParams.set("stage_pipeline_id", String(selectedStagePipelineId));
    }
    setSearchParams(nextParams, { replace: true });
  }, [filters, selectedFinalStatus, selectedStagePipelineId, setSearchParams]);

  const activeRows = leadAnalyticsStore.summaryRows;
  const activeStatusSummary = leadAnalyticsStore.statusSummary;
  const activePipelineStatusSummary = leadAnalyticsStore.pipelineStatusSummary;
  const activeAverages = leadAnalyticsStore.averages ?? defaultAverages;
  const activeLimit = leadAnalyticsStore.limit ?? 30;
  const activeTotalCount = leadAnalyticsStore.totalCount ?? 0;
  const sectionLabel = getSectionLabel(filters.section);
  const pipelineStatusGroups: LeadPipelineStatusSummaryGroup[] =
    activePipelineStatusSummary.length > 0
      ? activePipelineStatusSummary
      : activeStatusSummary.length > 0
        ? [
          {
            pipeline_id: 0,
            pipeline_name: "All Pipelines",
            total_count: activeTotalCount,
            status_summary: activeStatusSummary,
          },
        ]
        : [];

  const handleStatusCardClick = (pipelineId: number, status: string | null) => {
    leadAnalyticsStore.setSelectedFinalStatus(status);
    leadAnalyticsStore.setSelectedStagePipelineId(status ? pipelineId : null);
    const next = {
      ...filters,
      page_no: 1,
      final_status: status,
      stage_pipeline_id: status ? pipelineId : null,
    };
    leadAnalyticsStore.setFilters(next);
    void fetchData(next);
  };

  if (!userStore.hasPermission([EmployeeRoles.VIEW_SALES_DASHBOARD])) {
    return <PermissionBlockLabel />;
  }

  const downloadCsv = () => {
    const headers = [
      "Lead ID",
      "Owner Name",
      "MQL",
      "Non Contactable",
      "Open",
      "Total Time",
      "Final Stage",
      "Company Name",
      "Created Date",
      "Modified Date",
    ];

    const escapeCsvValue = (value: string | number) =>
      `"${String(value ?? "").replace(/"/g, "\"\"")}"`;

    const formatCsvDate = (value: string | null) => {
      if (!value) return "-";
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "-";
    };

    const csvRows = activeRows.map((row) =>
      [
        row.lead_id,
        row.owner_name || "-",
        row.mql,
        row.non_contactable,
        row.open,
        row.total_time,
        row.final_stage_name || "-",
        row.company_name || "-",
        formatCsvDate(row.created_date),
        formatCsvDate(row.modified_date),
      ]
        .map(escapeCsvValue)
        .join(",")
    );

    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
    const statusSuffix = selectedFinalStatus
      ? `_${selectedFinalStatus.replace(/\s+/g, "_").toLowerCase()}`
      : "";
    link.href = url;
    link.setAttribute(
      "download",
      `lead_stage_summary_${filters.section}${statusSuffix}_${timestamp}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <LoaderOverlay loading={loading} />
      <Box sx={{ bgcolor: "#F7F8FC", minHeight: "100vh" }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
          <Box>
            <CustomTypography variant="heading3" sx={{ mb: 2 }}>
              Lead Analytics Dashboard
            </CustomTypography>
          </Box>

          <Accordion
            expanded={isFiltersExpanded}
            onChange={(_, expanded) => leadAnalyticsStore.setIsFiltersExpanded(expanded)}
            sx={{
              mt: 0,
              borderRadius: "8px !important",
              border: "none",
              "&:before": { display: "none" },
              "& .MuiAccordionSummary-root": {
                minHeight: 38,
                py: 0,
              },
              "& .MuiAccordionSummary-content": {
                my: 0.5,
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <CustomTypography variant="heading7">Search & Filters</CustomTypography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
              <Grid2 container spacing={1.5} columns={12}>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <DateRangePickerNew
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                    onRangeSelect={(range: { startDate: Dayjs | null; endDate: Dayjs | null }) =>
                      leadAnalyticsStore.updateFilters((prev) => ({
                        ...prev,
                        start_date: range.startDate,
                        end_date: range.endDate,
                      }))
                    }
                  />
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <AppSearchDropdownMulti
                    title="Select owner"
                    list={ownerOptions}
                    value={selectedOwners}
                    local
                    isMultiSelect
                    handleSelect={(selectedItems) =>
                      handleOwnerChange(selectedItems?.map((item) => String(item.id)))
                    }
                    inputHeight="37px"
                    borderRadius="4px"
                  />
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <AppSearchDropdownMulti
                    title="Select pipeline"
                    list={pipelineOptions}
                    value={selectedPipelines}
                    local
                    isMultiSelect
                    handleSelect={(selectedItems) =>
                      handlePipelineChange(
                        (selectedItems ?? []).map((item) => String(item.id))
                      )
                    }
                    inputHeight="37px"
                    borderRadius="4px"
                  />
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <AppDropDown
                    title="Lead status"
                    isMultiSelect={false}
                    value={filters.section}
                    list={leadSectionFilterList}
                    onSingleSelectionChange={(id) => {
                      leadAnalyticsStore.updateFilters((prev) => ({
                        ...prev,
                        section: (id as LeadSection) || "all",
                      }));
                    }}
                    height="37px"
                    borderRadius="4px"
                    width="100%"
                  />
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <AppSearchDropdownMulti
                    title="Select source"
                    list={sourceOptions}
                    value={selectedSources}
                    local
                    isMultiSelect
                    handleSelect={(selectedItems) =>
                      handleSourceChange(
                        (selectedItems ?? []).map((item) => String(item.id))
                      )
                    }
                    inputHeight="37px"
                    borderRadius="4px"
                  />
                </Grid2>
                <Grid2
                  size={{ lg: 2, md: 4, sm: 6, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "stretch", sm: "flex-end" },
                    alignItems: "flex-end",
                    gap: 1,
                  }}
                >
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    onClick={() => {
                      leadAnalyticsStore.resetFilters();
                      leadAnalyticsStore.setSelectedFinalStatus(null);
                      leadAnalyticsStore.setSelectedStagePipelineId(null);
                      void fetchData({
                        ...leadAnalyticsStore.filters,
                        final_status: null,
                        stage_pipeline_id: null,
                      });
                    }}
                    customStyles={{ minWidth: 88, flex: { xs: 1, sm: "0 0 auto" } }}
                  />
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={() => {
                      const next = {
                        ...filters,
                        page_no: 1,
                        final_status: selectedFinalStatus,
                        stage_pipeline_id: selectedStagePipelineId,
                      };
                      leadAnalyticsStore.setFilters(next);
                      void fetchData(next);
                    }}
                    customStyles={{ minWidth: 88, flex: { xs: 1, sm: "0 0 auto" } }}
                  />
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>

          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <CustomTypography variant="heading6">
              Final Status Summary ({sectionLabel})
            </CustomTypography>
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
              {pipelineStatusGroups.map((pipelineGroup, index) => (
                <Box key={`${pipelineGroup.pipeline_id}-${pipelineGroup.pipeline_name}`}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <CustomTypography variant="heading7" sx={{ color: "#1E2A44" }}>
                      {pipelineGroup.pipeline_name}
                    </CustomTypography>
                    <CustomTypography variant="body2" sx={{ color: "#5C6785", fontSize: "0.78rem" }}>
                      {pipelineGroup.total_count} leads
                    </CustomTypography>
                  </Box>
                  <Grid2 container spacing={1} columns={12}>
                    {renderStatusSummaryCards(
                      pipelineGroup.pipeline_id,
                      pipelineGroup.status_summary,
                      selectedStagePipelineId,
                      selectedFinalStatus,
                      handleStatusCardClick
                    )}
                  </Grid2>
                  {index < pipelineStatusGroups.length - 1 && (
                    <Divider sx={{ mt: 2.5, borderColor: "#E2E8F4" }} />
                  )}
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid2 container spacing={1.5} columns={12}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.1, borderRadius: 2, borderColor: "#DDE3F5", bgcolor: "#FBFCFF" }}
                >
                  <CustomTypography variant="body2" sx={{ fontSize: "0.74rem", color: "#5C6785" }}>
                    Avg MQL (hrs)
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.6, fontSize: "1rem", color: "#1F2A44" }}>
                    {activeAverages.mql.toFixed(2)}
                  </CustomTypography>
                </Paper>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.1, borderRadius: 2, borderColor: "#DDE3F5", bgcolor: "#FBFCFF" }}
                >
                  <CustomTypography variant="body2" sx={{ fontSize: "0.74rem", color: "#5C6785" }}>
                    Avg Non Contactable (hrs)
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.6, fontSize: "1rem", color: "#1F2A44" }}>
                    {activeAverages.non_contactable.toFixed(2)}
                  </CustomTypography>
                </Paper>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.1, borderRadius: 2, borderColor: "#DDE3F5", bgcolor: "#FBFCFF" }}
                >
                  <CustomTypography variant="body2" sx={{ fontSize: "0.74rem", color: "#5C6785" }}>
                    Avg Open (hrs)
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.6, fontSize: "1rem", color: "#1F2A44" }}>
                    {activeAverages.open.toFixed(2)}
                  </CustomTypography>
                </Paper>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.1, borderRadius: 2, borderColor: "#DDE3F5", bgcolor: "#FBFCFF" }}
                >
                  <CustomTypography variant="body2" sx={{ fontSize: "0.74rem", color: "#5C6785" }}>
                    Avg Total Time (hrs)
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.6, fontSize: "1rem", color: "#1F2A44" }}>
                    {activeAverages.total_time.toFixed(2)}
                  </CustomTypography>
                </Paper>
              </Grid2>
            </Grid2>

          </Paper>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <AppButton
              variant="primary"
              label="Download CSV"
              onClick={downloadCsv}
            />
          </Box>

          <LeadStageSummaryTable
            rows={activeRows}
            page_no={filters.page_no}
            limit={activeLimit}
            total_count={activeTotalCount}
            sortBy={filters.sort_by}
            sortType={filters.sort_type}
            onSortChange={(column, sortType) => {
              const next = {
                ...filters,
                sort_by: column,
                sort_type: sortType,
                page_no: 1,
                final_status: selectedFinalStatus,
                stage_pipeline_id: selectedStagePipelineId,
              };
              leadAnalyticsStore.setFilters(next);
              void fetchData(next);
            }}
            onPageChange={(_event, page) => {
              const next = {
                ...filters,
                page_no: page,
                final_status: selectedFinalStatus,
                stage_pipeline_id: selectedStagePipelineId,
              };
              leadAnalyticsStore.setFilters(next);
              void fetchData(next);
            }}
            onRowsPerPageChange={(limit) => {
              const next = {
                ...filters,
                limit,
                page_no: 1,
                final_status: selectedFinalStatus,
                stage_pipeline_id: selectedStagePipelineId,
              };
              leadAnalyticsStore.setFilters(next);
              void fetchData(next);
            }}
          />
        </Paper>
      </Box>
    </>
  );
};

export default observer(LeadAnalyticsDashboard);
